import React from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageSquare, Send, Clock } from 'lucide-react';
import { ROLES } from '../mock';

const fmtTs = (ts) => {
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const ConversationLog = ({ session, canEdit, onUpdate }) => {
  const { appendConversation } = useApp();
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const listRef = React.useRef(null);
  const log = session.conversation_log || [];

  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [log.length]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const updated = await appendConversation(session.id, t);
      if (updated && typeof onUpdate === 'function') onUpdate(updated);
      setText('');
    } catch (err) {
      console.error('[ConversationLog] Error adding conversation:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-brand-border">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="h-4 w-4 text-brand-indigo" />
          <div className="text-sm font-semibold text-brand-indigo">Conversation Log</div>
          <span className="text-[11px] text-muted-foreground ml-auto">{log.length} entr{log.length === 1 ? 'y' : 'ies'}</span>
        </div>
        <div ref={listRef} className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
          {log.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No entries yet. Capture what was discussed during the meeting for the audit record.</div>
          )}
          {log.map((e, i) => (
            <div key={i} className="border border-brand-border rounded-md p-3 bg-white">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                <Clock className="h-3 w-3" />
                <span>{fmtTs(e.ts)}</span>
                <span>·</span>
                <span className="font-semibold text-brand-indigo">{e.author}</span>
              </div>
              <div className="text-sm text-brand-indigo/90 leading-relaxed whitespace-pre-wrap">{e.text}</div>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="mt-3 space-y-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Note down what was discussed, decisions taken, action items agreed…"
              className="min-h-[72px]"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) add(); }}
            />
            <div className="flex items-center justify-between">
              <div className="text-[10px] text-muted-foreground">Ctrl/Cmd + Enter to add. Timestamped and immutable in the record.</div>
              <Button onClick={add} disabled={!text.trim() || busy} className="bg-brand-indigo hover:bg-brand-indigoLight text-white h-9">
                <Send className="h-3.5 w-3.5 mr-2" /> {busy ? 'Adding\u2026' : 'Add entry'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationLog;
